<template>
  <div>
    <div class="content-header">
      <div class="container-fluid">
        <div class="row mb-2">
          <div class="col-sm-6">
            <h1 class="m-0">{{ this.$route.name }}</h1>
          </div>
          <div class="col-sm-6">
            <ol class="breadcrumb float-sm-right">
              <li class="breadcrumb-item active"><i v-bind:class="routeIcon"></i> {{ $t(this.$route.name) }}</li>
            </ol>
          </div>
        </div>
      </div>
    </div>

    <section class="content">
      <div class="container-fluid">
        <div class="row col-lg-12">
          <div class="form-group col-sm-2">
            <EditForm
                :default-values="{}"
                create
                title="Create partner"
                @finish="handleItemsUpdate"
            >
              <template #button="{onclick}">
                <b-button type="button" variant="success" @click="onclick"><i class="fas fa-plus"></i> Create</b-button>
              </template>
            </EditForm>
          </div>
        </div>

        <b-row class="col-lg-12">
          <div class="col-lg-3 col-md-3 col-sm-12" v-for="({id, label}) in filterInputs" v-bind:key="id">
            <b-form-group label-cols-lg="3" :label-for="id + '-filter-input'" :label="label">
              <b-form-input :id="id + '-filter-input'" v-model="filters[id]" autocomplete="off" :placeholder="'Enter ' + label"/>
            </b-form-group>
          </div>
        </b-row>

        <b-row class="col-lg-12">
          <div class="col-lg-12 col-md-12 col-sm-12 apply-clear">
            <FilterButtons
                :cancel="this.cancel"
                :loadFn="loadItems"
                :cancelFn="cancelRequest"
                :resetFn="clearFilters"
            />
          </div>
        </b-row>

        <div class="row col-12" id="loading-container">
          <div class="col-12 mb-10">
            <div class="mb-10">
              <div class="table-wrapper mb-10">
                <b-table
                    :striped="true"
                    :small="true"
                    :hover="true"
                    :empty-text="emptyText"
                    show-empty
                    :busy="!!cancel"
                    :items="items"
                    :fields="fields"
                    :sort-by="this.sorts.by"
                    :sort-desc="this.sorts.desc"
                    :no-local-sorting="true"
                    :sort-icon-left="true"
                    @sort-changed="sortingChanged"
                >
                  <template #cell(actions)="row">
                    <EditForm
                        :default-values="row.item"
                        title="Edit partner"
                        @finish="handleItemsUpdate"
                    />
                    <RemoveForm :object="row.item.displayName"
                                message="Please confirm removing partner"
                                :url="'/api/v1/partners/' + row.item.id"
                                @finish="handleItemsUpdate"
                    />
                  </template>
                </b-table>
              </div>
            </div>

            <div class="row mb-10">
              <div class="col-12">
                <b-pagination
                    class="float-right"
                    v-model="pagination.page"
                    :total-rows="pagination.records"
                    :per-page="pagination.onPage"
                    @change="onPageChange"
                    first-number
                    last-number
                />
              </div>
            </div>

            <div class="row mb-10">
              <div class="col-12">
                <ve-pagination
                    class="float-right"
                    :page-index="pagination.page"
                    :page-size="pagination.onPage"
                    :total="pagination.records"
                    :pagingCount="pagination.count"
                    :layout="['total', 'sizer']"
                    :pageSizeOption="pagination.pageSizeOption"
                    @on-page-size-change="onPageSizeChange"
                />
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import EditPartnerForm from "../../components/Partners/EditPartnerForm.vue";
import RemoveForm from "../../components/remove-form/RemoveForm.vue";
import axios from "axios";
import {getRouteIcon, ROUTE_PARTNERS} from "../../utils/routerIcons";
import Multiselect from "vue-multiselect";
import FilterButtons from "../../components/FilterButtons.vue";

function getDefaultPagination() {
  return {
    onPage: 10,
    page: 1,
    records: 0,
    count: 5,
    pageSizeOption: [10,25,50,100,200],
  };
}

function getDefaultFilters() {
  return {
    id: null,
    displayName: null,
    businessName: null,
    contactPerson: null,
    contactEmail: null,
    address: null,
    notes: null,
  };
}

function getDefaultSorts() {
  return {
    by: null,
    desc: true,
  }
}

export default {
  components: {
    EditForm: EditPartnerForm,
    RemoveForm,
    Multiselect,
    FilterButtons,
  },
  data() {
    return {
      cancel: null,
      error: null,
      fields: [
        { key: 'id', label: 'Id', sortable: false },
        { key: 'displayName', label: 'Display name', sortable: true, },
        { key: 'businessName', label: 'Business name', sortable: true, },
        { key: 'contactPerson', label: 'Contact person', sortable: true, },
        { key: 'contactEmail', label: 'Contact email', sortable: true, },
        { key: 'address', label: 'Address', },
        { key: 'notes', label: 'Notes', },
        { key: 'actions', label: 'Actions', }
      ],
      filterInputs: [
        {label: 'Id', id: 'id'},
        {label: 'Display name', id: 'displayName'},
        {label: 'Business name', id: 'businessName'},
        {label: 'Contact person', id: 'contactPerson'},
        {label: 'Contact email', id: 'contactEmail'},
        {label: 'Address', id: 'address'},
        {label: 'Notes', id: 'notes'},
      ],
      filters: getDefaultFilters(),
      items: [],
      pagination: getDefaultPagination(),
      sorts: getDefaultSorts(),
    }
  },
  computed: {
    emptyText() {
      if (this.cancel) {
        return 'Loading items..';
      }

      if (this.error) {
        return this.error;
      }

      return 'There are no records to show';
    },
    routeIcon: () => getRouteIcon(ROUTE_PARTNERS),
  },
  methods: {
    clearFilters() {
      this.filters = getDefaultFilters();
      this.sortingChanged({
        sortBy: null,
        sortDesc: null
      })
    },
    sortingChanged(ctx) {
      this.sorts.by = ctx.sortBy;
      this.sorts.desc = ctx.sortDesc;
      this.pagination.page = 1;

      this.loadItems();
    },
    handleItemsUpdate() {
      this.loadItems();
    },
    loadItems() {
      let loadingInstance = this.$veLoading({
        target: document.querySelector("#loading-container"),
        name: "wave",
      });

      this.items = [];

      let self = this;
      loadingInstance.show();

      this
          .$http({
            url: '/api/v1/partners',
            params: {
              ...this.filters,
              // pagination
              limit: this.pagination.onPage,
              offset: this.pagination.onPage * (this.pagination.page - 1),

              // sort params
              orderBy: this.sorts.by,
              direction: this.sorts.desc ? 'DESC' : 'ASC',
            },
            method: 'GET',
            cancelToken: new axios.CancelToken(c => {self.cancel = c})
          })
          .then(resp => {
            let {items, pagination: {limit, totalCount}} = resp.data;

            this.items = items;
            this.pagination.onPage = limit;
            this.pagination.records = totalCount;
          })
          .catch((err) => {
            this.items = [];
            this.error = err;
          })
          .finally(() => {
            loadingInstance.close();
            this.cancel = null;
          });
    },
    cancelRequest() {
      if (this.cancel) {
        this.cancel('this -> cancel previous request');
      }
    },
    onPageChange(page) {
      this.pagination.page = page;
      this.loadItems()
    },
    onPageSizeChange(pageSize) {
      this.pagination.page = 1;
      this.pagination.onPage = pageSize;
      this.loadItems();
    },
    onFinishEdit() {
      this.loadItems();
    },
  },
  mounted() {
    this.loadItems();
  }
}
</script>
